import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';
import { DinoId, DINOS } from '../data/dinos';
import { GameMode } from './GameScene';
import { CommsBox, COMMS } from '../objects/CommsBox';

interface OverInit {
  score: number;
  wave: number;
  dinoId: DinoId;
  ammoLeft: number;
  mode: GameMode;
}

const STAR_BONUS = 10;

export class GameOverScene extends Phaser.Scene {
  private payload!: OverInit;

  constructor() {
    super('GameOver');
  }

  init(data: OverInit) {
    this.payload = {
      score: data.score ?? 0,
      wave: data.wave ?? 1,
      dinoId: data.dinoId ?? 'doux',
      ammoLeft: data.ammoLeft ?? 0,
      mode: data.mode ?? 'practice',
    };
  }

  create() {
    const { score: baseScore, wave, dinoId, ammoLeft, mode } = this.payload;
    const bonus = ammoLeft * STAR_BONUS;
    const finalScore = baseScore + bonus;
    this.cameras.main.setBackgroundColor(0x0d0a1a);

    const accent = DINOS[dinoId].stripe;

    this.add
      .text(GAME_WIDTH / 2, 28, 'GAME OVER', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '20px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const dino = this.add
      .sprite(GAME_WIDTH / 2, 68, `dino-${dinoId}`)
      .setScale(3)
      .play(`${dinoId}-hurt`);
    dino.anims.stopOnFrame(dino.anims.currentAnim!.frames[3]);
    dino.setTint(0x9988aa);

    // "BOB fell on GLACIER." — bumped from 9px@70% (illegible) to 12px@100%
    // with a stroke, so it actually reads as the headline of the screen.
    this.add
      .text(GAME_WIDTH / 2, 102, `${DINOS[dinoId].name} fell on ${DINOS[dinoId].planet}.`, {
        fontFamily: '"VT323", monospace',
        fontSize: '13px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5);

    // Score breakdown column.
    const colY = 130;
    const labelX = GAME_WIDTH / 2 - 80;
    const valueX = GAME_WIDTH / 2 + 80;
    const row = (i: number) => colY + i * 18;

    this.add
      .text(labelX, row(0), 'run score', {
        fontFamily: '"VT323", monospace',
        fontSize: '12px',
        color: '#ffffff',
      })
      .setOrigin(0, 0);
    this.add
      .text(valueX, row(0), String(baseScore).padStart(4, '0'), {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '12px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0);

    this.add
      .text(labelX, row(1), `unfired stars × ${STAR_BONUS}`, {
        fontFamily: '"VT323", monospace',
        fontSize: '12px',
        color: '#ffe23a',
      })
      .setOrigin(0, 0);
    const bonusValue = this.add
      .text(valueX, row(1), '+0', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '12px',
        color: '#ffe23a',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0);

    this.add
      .rectangle(GAME_WIDTH / 2, row(2) + 8, 200, 1, 0xffffff, 0.5)
      .setOrigin(0.5);

    this.add
      .text(labelX, row(2) + 14, 'TOTAL', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '13px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);
    const totalValue = this.add
      .text(valueX, row(2) + 12, String(baseScore).padStart(4, '0'), {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '16px',
        color: '#ffe23a',
        stroke: '#000000',
        strokeThickness: 3,
        fontStyle: 'bold',
      })
      .setOrigin(1, 0);

    this.add
      .text(GAME_WIDTH / 2, row(4) + 14, `wave ${wave}${mode === 'gauntlet' ? '  /  gauntlet' : ''}`, {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#9ad8ff',
      })
      .setOrigin(0.5, 0);

    // Comms — Mika reacts to the loss with the per-dino mayday line.
    const comms = new CommsBox(this);
    comms.say(COMMS.gameOver(dinoId));

    // Buttons. RETRY restarts the same mode that died. Label changes
    // based on mode so the player knows whether they're being sent back
    // to the start of a fresh gauntlet (always Bob/Glacier, regardless of
    // which dino died) or just retrying their solo planet attempt.
    const retryLabel = mode === 'gauntlet' ? 'GAUNTLET' : 'RETRY';
    this.makeButton(GAME_WIDTH / 2 - 60, GAME_HEIGHT - 24, retryLabel, accent, () => {
      if (mode === 'gauntlet') {
        // Gauntlet retry ALWAYS restarts from Bob/doux — that's the start
        // of the gauntlet, regardless of which leg the player died on.
        this.scene.start('Game', { dinoId: 'doux', mode: 'gauntlet', runScore: 0 });
      } else {
        this.scene.start('Game', { dinoId, mode: 'practice', runScore: 0 });
      }
    });
    this.makeButton(GAME_WIDTH / 2 + 60, GAME_HEIGHT - 24, 'LEVELS', 0x444466, () =>
      this.scene.start('CharacterSelect'),
    );

    // Animate bonus tally.
    if (ammoLeft > 0) {
      const obj = { val: 0 };
      this.tweens.add({
        targets: obj,
        val: bonus,
        duration: Math.min(1400, 80 * ammoLeft + 300),
        ease: 'Cubic.easeOut',
        onUpdate: () => {
          const v = Math.round(obj.val);
          bonusValue.setText(`+${v}`);
          totalValue.setText(String(baseScore + v).padStart(4, '0'));
        },
      });
    }
    void finalScore;

    this.input.keyboard!.once('keydown-ENTER', () => {
      if (mode === 'gauntlet') {
        this.scene.start('Game', { dinoId: 'doux', mode: 'gauntlet', runScore: 0 });
      } else {
        this.scene.start('Game', { dinoId, mode: 'practice', runScore: 0 });
      }
    });
    this.input.keyboard!.once('keydown-ESC', () => this.scene.start('CharacterSelect'));
  }

  private makeButton(x: number, y: number, label: string, color: number, onClick: () => void) {
    const w = 90;
    const h = 22;
    const bg = this.add.rectangle(x, y, w, h, color).setStrokeStyle(2, 0xffffff);
    this.add
      .text(x, y, label, {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setScale(1.05));
    bg.on('pointerout', () => bg.setScale(1));
    bg.on('pointerdown', onClick);
    return bg;
  }
}
